import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme,
  Tabs,
  Tab,
  Autocomplete,
  FormControlLabel,
  Switch as MuiSwitch,
  styled,
  CardContent
} from '@mui/material';
import { useNavigationGuard } from '@Hooks/useNavigationGuard';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import PageTitle from '@Components/PageTitle/PageTitle';
import LoadingButton from '@mui/lab/LoadingButton';
import LoadingComponent from '@Components/LoadingComponent/LoadingComponent';
import ExcelBulkImport from '@Components/ExcelBulkImport';
import useGetAll from '@Hooks/useGetAll';
import usePostItem from '@Hooks/usePostItem';
import axiosInstance from '@API/axios';
import * as APIRoutes from '@API/APIRoutes';
import * as DefaultRoutes from '@Routes/DefaultRoutes';
import { setSnackbar } from '@Redux/snackBarSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
  PageContainer,
  HeaderContainer,
  ContentGridContainer,
  SubmitButtonContainer,
  cardTransition,
  cardContentStyles,
  sectionTitleStyles,
  labelTextStyles,
  valueTextStyles,
  smallChipStyles,
  descriptionPaperStyles,
  tableHeaderCellStyles,
  tableCellStyles,
  dialogTitleStyles,
  dialogButtonStyles,
  cancelDialogButtonStyles,
  addButtonStyles,
  actionButtonStyles,
  noCommentStyles
} from '@Pages/RnDMember/ExitRequestResponseUnified/ExitRequestResponseStyles';
import { getRestrictionTypeColor } from '../../../Utils/Validations/HelperFunctions/StyleFunction';
import themeCreator from '@Themes/themeCreator';

const CoordinatorResponsePage = () => {
  const { reqId, categoryAssignmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { loading: loadingProducts, getAll } = useGetAll();
  const { loading: submitting, postItem } = usePostItem();

  // Data states
  const [loading, setLoading] = useState(true);
  const [requestData, setRequestData] = useState(null);
  const [responseId, setResponseId] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [products, setProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockReason, setLockReason] = useState('');
  const [isEditMode, setIsEditMode] = useState(true); // Default to edit mode for coordinator to add products
  const [initialProducts, setInitialProducts] = useState([]); // Track initial products for change detection

  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productBrand, setProductBrand] = useState('');
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [addingCustomProduct, setAddingCustomProduct] = useState(false);
  const [isCustomProduct, setIsCustomProduct] = useState(false);
  const [customProductName, setCustomProductName] = useState('');
  const [customProductCUC, setCustomProductCUC] = useState('');
  const [customProductErrors, setCustomProductErrors] = useState({ name: '', cuc: '' });

  // Product dialog states
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductBrand, setEditProductBrand] = useState('');
  const [showEditProductDialog, setShowEditProductDialog] = useState(false);

  const isFromResponses = location.state?.from === 'responses' || location.pathname.includes('coordinator-responses');

  const countriesInMyRegions = useMemo(() => {
    return requestData?.countries || [];
  }, [requestData?.countries]);

  // Check if products have changed from initial state
  const hasUnsavedChanges = useMemo(() => {
    // Different number of products = has changes
    if (initialProducts.length !== products.length) return true;

    // Create maps for comparison
    const initialMap = new Map(initialProducts.map(p => [(p.productId || p.id), p.brand]));
    const currentMap = new Map(products.map(p => [(p.productId || p.id), p.brand]));

    // Check if all initial products are still there with same brands
    for (const [id, brand] of initialMap.entries()) {
      if (!currentMap.has(id)) return true; // Product was removed
      if (currentMap.get(id) !== brand) return true; // Brand was changed
    }

    // Check if any new products were added
    for (const id of currentMap.keys()) {
      if (!initialMap.has(id)) return true; // Product was added
    }

    return false;
  }, [products, initialProducts]);

  // Navigation guard (handles browser refresh/close and navigation)
  useNavigationGuard(hasUnsavedChanges && isEditMode && !isLocked, 'You have unsaved changes. Please save your draft or submit before leaving.');
  // ── Fetch data on mount ──────────────────────────────────────────
  useEffect(() => {
    const fetchAll = async () => {
      // Load available products
      getAll('/product/all', 'Failed to fetch products').then(res => {
        if (res) {
          setAvailableProducts(res.map(p => ({
            productId: p.id,
            productCucNumber: p.cucNumber,
            productName: p.name
          })));
        }
      });

      // Load existing coordinator response (if any)
      try {
        setLoading(true);
        // Fetch exit request details
        const reqRes = await axiosInstance.get(`/exit-request-response/my-submissions/${reqId}/${categoryAssignmentId}`);
        const data = reqRes.data;
        if (data?.exitRequest) {
          setRequestData(data.exitRequest);
          setInitialProducts([]); // Reset initial products for new response
        }

        // Fetch coordinator response for this assignment
        const coordRes = await axiosInstance.get(
          APIRoutes.COORDINATOR_RESPONSE_BY_ASSIGNMENT(categoryAssignmentId)
        );
        if (coordRes.data) {
          const cr = coordRes.data;
          setResponseId(cr.id);
          setFeedback(cr.additionalFeedback || '');
          setProducts(cr.products || []);
          setInitialProducts(cr.products || []); // Set initial products for change detection
          if (cr.status === 'COORDINATOR_SUBMITTED') {
            setIsSubmitted(true);
            setIsEditMode(false); // View mode for submitted responses
          }
        }

        // Check lock status from the exit request
        const reqInfoRes = await axiosInstance.get(`/exitRequest/${reqId}`);
        if (reqInfoRes.data) {
          const req = reqInfoRes.data;
          // Merge with existing requestData to preserve categoryName
          setRequestData(prev => ({
            ...prev,
            ...req,
            categoryName: prev.categoryName || req.categoryName // Preserve categoryName from first call
          }));
          // Check if request is closed or cancelled
          if (req.status === 'CLOSED') {
            setIsLocked(true);
            setLockReason('This exit request has been closed. No further edits are allowed.');
          } else if (req.status === 'CANCELLED') {
            setIsLocked(true);
            setLockReason('This exit request has been cancelled. No further edits are allowed.');
          }
        }
      } catch (err) {
        console.log('No coordinator response yet or fetching request info failed:', err?.response?.status);
        try {
          const reqInfoRes = await axiosInstance.get(`/exitRequest/${reqId}`);
          if (reqInfoRes.data) {
            const req = reqInfoRes.data;
            setRequestData(req);
            if (req.status === 'CLOSED') {
              setIsLocked(true);
              setLockReason('This exit request has been closed. No further edits are allowed.');
            } else if (req.status === 'CANCELLED') {
              setIsLocked(true);
              setLockReason('This exit request has been cancelled. No further edits are allowed.');
            }
          }
        } catch (e) {
          console.error('Could not load request info', e);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [reqId, categoryAssignmentId]);

  // ── Navigation ────────────────────────────────────────────────────
  const handleBack = () => {
    if (isFromResponses) {
      navigate(DefaultRoutes.rdCoordinatorResponses);
    } else {
      navigate(DefaultRoutes.rdCoordinatorIncomingRequests);
    }
  };

  const handleModeToggle = () => {
    if (!isLocked && !isSubmitted) {
      setIsEditMode(!isEditMode);
    }
  };

  // ── Add product ───────────────────────────────────────────────────
  const handleAddProduct = async () => {
    if (isCustomProduct) {
      let errors = {};
      let hasError = false;
      if (!customProductName.trim()) { errors.name = "Product name is required"; hasError = true; }
      if (!customProductCUC.trim()) { errors.cuc = "CUC number is required"; hasError = true; }
      else {
        const dup = availableProducts.find(p => p.productCucNumber?.trim() === customProductCUC.trim());
        if (dup) { errors.cuc = `CUC "${customProductCUC.trim()}" already exists: ${dup.productName}`; hasError = true; }
      }
      if (hasError) { setCustomProductErrors(errors); return; }

      setAddingCustomProduct(true);
      try {
        await postItem('/product/add',
          { name: customProductName.trim(), cucNumber: customProductCUC.trim() },
          (data) => {
            const newProduct = {
              productId: data,
              productCucNumber: customProductCUC.trim(),
              productName: customProductName.trim(),
              brand: productBrand
            };
            setProducts(prev => [...prev, newProduct]);
            setAvailableProducts(prev => [...prev, { productId: data, productCucNumber: customProductCUC.trim(), productName: customProductName.trim() }]);
            closeAddDialog();
          },
          'Product added successfully!',
          'Failed to add product.'
        );
      } catch (err) {
        dispatch(setSnackbar({ open: true, message: err?.response?.data || 'Failed to add product', error: true }));
      } finally {
        setAddingCustomProduct(false);
      }
    } else if (selectedProduct) {
      const alreadyAdded = products.some(p => (p.productId || p.id) === selectedProduct.productId);
      if (alreadyAdded) {
        dispatch(setSnackbar({ open: true, message: 'This product is already in the list.', error: true }));
        return;
      }
      setProducts(prev => [...prev, { ...selectedProduct, brand: productBrand }]);
      closeAddDialog();
    }
  };

  const closeAddDialog = () => {
    setShowAddDialog(false);
    setSelectedProduct(null);
    setProductBrand('');
    setIsCustomProduct(false);
    setCustomProductName('');
    setCustomProductCUC('');
    setCustomProductErrors({ name: '', cuc: '' });
  };

  const handleRemoveProduct = (productId) => {
    setProducts(prev => prev.filter(p => (p.productId || p.id) !== productId));
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setEditProductBrand(product.brand || '');
    setShowEditProductDialog(true);
  };

  const handleSaveEditProduct = () => {
    if (!editingProduct) return;
    const updatedProducts = products.map(p => {
      if ((p.productId || p.id) === (editingProduct.productId || editingProduct.id)) {
        return { ...p, brand: editProductBrand };
      }
      return p;
    });
    setProducts(updatedProducts);
    setShowEditProductDialog(false);
    setEditingProduct(null);
    setEditProductBrand('');
  };

  const handleCancelEditProduct = () => {
    setShowEditProductDialog(false);
    setEditingProduct(null);
    setEditProductBrand('');
  };

  // ── Excel bulk import ────────────────────────────────────────────
  const handleBulkProductsImported = (importedProducts) => {
    if (!importedProducts || importedProducts.length === 0) return;
    const existingIds = new Set(products.map(p => p.productCucNumber || p.cucNumber));
    const newProds = importedProducts.filter(p => !existingIds.has(p.productCucNumber));
    if (newProds.length === 0) {
      dispatch(setSnackbar({ open: true, message: 'All imported products are already in the list.', error: false }));
      return;
    }
    setProducts(prev => [...prev, ...newProds]);
    dispatch(setSnackbar({ open: true, message: `${newProds.length} product(s) imported.`, error: false }));
  };

  // ── Build payload ─────────────────────────────────────────────────
  const buildPayload = () => ({
    id: responseId || null,
    exitRequestId: parseInt(reqId),
    categoryAssignmentId: parseInt(categoryAssignmentId),
    additionalFeedback: feedback,
    isCompliant: true,
    products: products.map(p => ({
      productId: p.productId || p.id || null,
      productName: p.productName || p.name || '',
      productCucNumber: p.productCucNumber || p.cucNumber || '',
      isAffected: null,
      comment: '',
      brand: p.brand || '',
      countryIds: (p.countries || []).map(c => c.id || c)
    }))
  });

  // ── Save draft ────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (isSubmitted) {
      dispatch(setSnackbar({ open: true, message: 'This impact assessment has already been submitted and cannot be edited.', error: true }));
      return;
    }

    setSavingDraft(true);
    try {
      const payload = buildPayload();
      const res = await axiosInstance.post(APIRoutes.COORDINATOR_RESPONSE_SAVE, payload);
      setResponseId(res.data.id);
      setInitialProducts([...products]); // Update initial products after successful save
      dispatch(setSnackbar({ open: true, message: 'Draft saved successfully!', error: false }));
    } catch (err) {
      dispatch(setSnackbar({ open: true, message: err?.response?.data || 'Failed to save draft', error: true }));
    } finally {
      setSavingDraft(false);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (isSubmitted) {
      dispatch(setSnackbar({ open: true, message: 'This impact assessment has already been submitted and cannot be submitted again.', error: true }));
      setShowSubmitConfirm(false);
      return;
    }

    if (products.length === 0) {
      dispatch(setSnackbar({ open: true, message: 'Please add at least one product before submitting.', error: true }));
      return;
    }

    setSavingDraft(true);
    try {
      const payload = buildPayload();
      const saveRes = await axiosInstance.post(APIRoutes.COORDINATOR_RESPONSE_SAVE, payload);
      const savedId = saveRes.data.id || responseId;
      setResponseId(savedId);

      const submitRes = await axiosInstance.post(APIRoutes.COORDINATOR_RESPONSE_SUBMIT(savedId));
      setIsSubmitted(true);
      setIsEditMode(false);
      setShowSubmitConfirm(false);
      dispatch(setSnackbar({ open: true, message: 'Product list submitted successfully! R&D members have been notified.', error: false }));
    } catch (err) {
      dispatch(setSnackbar({ open: true, message: err?.response?.data || 'Failed to submit', error: true }));
    } finally {
      setSavingDraft(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <LoadingComponent size="4em" thickness={5} color="primary" />
      </Box>
    );
  }

  const ingredient = requestData?.ingredient;
  const user = requestData?.user;

  // Product table component (matches RD response page)
  const ProductTable = ({ products, onRemove, onEdit, isEditMode }) => {
    const theme = useTheme();

    return (
      <Paper variant="outlined" sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{
          display: 'flex',
          width: '100%',
          backgroundColor: theme.palette.grey[100],
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: 10
        }}>
          <Box sx={{ flex: '1 0 20%', ...tableHeaderCellStyles(theme) }}>CUC Number</Box>
          <Box sx={{ flex: '1 0 35%', ...tableHeaderCellStyles(theme) }}>Name</Box>
          <Box sx={{ flex: '1 0 35%', ...tableHeaderCellStyles(theme) }}>Brand</Box>
          {isEditMode && <Box sx={{ flex: '0 0 10%', ...tableHeaderCellStyles(theme), textAlign: 'center' }}>Actions</Box>}
        </Box>

        <Box sx={{
          width: '100%',
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {products.map((product, index) => (
            <Box
              key={product.productId || product.id}
              sx={{
                display: 'flex',
                width: '100%',
                bgcolor: index % 2 === 1 ? theme.palette.grey[50] : 'white',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ flex: '1 0 20%', ...tableCellStyles(theme) }}>{product.productCucNumber}</Box>
              <Box sx={{ flex: '1 0 35%', ...tableCellStyles(theme) }}>{product.productName}</Box>
              <Box sx={{ flex: '1 0 35%', ...tableCellStyles(theme) }}>
                {product.brand || (
                  <Typography component="span" sx={noCommentStyles(theme)}>—</Typography>
                )}
              </Box>
              {isEditMode && (
                <Box sx={{ flex: '0 0 10%', ...tableCellStyles(theme), textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => onEdit(product)}
                    title="Edit product"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onRemove(product.productId || product.id)}
                    title="Remove product"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Paper>
    );
  };

  return (
    <PageContainer>
      {/* Page Header */}
      <HeaderContainer>
        <PageTitle
          title="Impact Assessment"
          PageIcon={<InfoIcon color="white" fontSize="large" />}
          iconColor={themeCreator().palette.primary.main}
          noMargin
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
              mr: 1,
              '&:hover': {
                backgroundColor: '#ffffff',
                color: theme.palette.primary.main,
                boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
              }
            }}
          >
            Return
          </Button>
          {responseId && !isLocked && !isSubmitted && (
            <FormControlLabel
              control={
                <ModeSwitch
                  checked={!isEditMode}
                  onChange={handleModeToggle}
                />
              }
              label={
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: isEditMode ? theme.palette.primary.main : theme.palette.secondary.main
                }}>
                  {isEditMode ? (
                    <>
                      <EditIcon sx={{ fontSize: '0.9rem' }} />
                      <Typography component="span" sx={{ fontSize: '0.75rem' }}>Edit</Typography>
                    </>
                  ) : (
                    <>
                      <VisibilityIcon sx={{ fontSize: '0.9rem' }} />
                      <Typography component="span" sx={{ fontSize: '0.75rem' }}>View</Typography>
                    </>
                  )}
                </Box>
              }
              labelPlacement="end"
              sx={{ m: 0 }}
            />
          )}
          {isSubmitted && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Submitted"
              color="success"
              sx={{ fontWeight: 700, ml: 1 }}
            />
          )}
        </Box>
      </HeaderContainer>

      {/* Request Details Section */}
      {requestData && (
        <ContentGridContainer>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <StyledCard sx={cardTransition(theme)}>
                <CardContent sx={{ ...cardContentStyles, py: 0.8 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SectionTitle variant="h6" sx={sectionTitleStyles(theme)}>
                      Request Details
                    </SectionTitle>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ ml: 1, pb: 1.3, fontSize: '0.75rem' }}
                    >
                      Issued at: {requestData && requestData.createdAt ? new Date(requestData.createdAt).toLocaleDateString() : "-"}
                    </Typography>
                  </Box>

                  <Grid container spacing={1.5}>
                    {/* LEFT HALF */}
                    <Grid item xs={12} sm={6}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 1, minWidth: '80px', flexShrink: 0 }}>Ingredient Name:</Typography>
                            <ValueText variant="body1" sx={{ ...valueTextStyles, flex: 1 }}>{ingredient?.name || '—'}</ValueText>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 1, minWidth: '80px' }}>CAS Number:</Typography>
                            <ValueText variant="body1" sx={valueTextStyles}>{ingredient?.casNumber || '—'}</ValueText>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 1, minWidth: '80px' }}>Restriction:</Typography>
                            <Chip
                              label={requestData.restrictionType?.replace(/_/g, ' ') || '—'}
                              color={getRestrictionTypeColor(requestData.restrictionType)}
                              size="small"
                              sx={smallChipStyles}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 1, minWidth: '80px' }}>Primary Date:</Typography>
                            <ValueText variant="body1" color="error" sx={valueTextStyles}>{requestData.primaryDate ? new Date(requestData.primaryDate).toLocaleDateString() : "-"}</ValueText>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 1, minWidth: '80px' }}>Secondary Date:</Typography>
                            <ValueText variant="body1" sx={{ ...valueTextStyles, color: theme.palette.warning.main }}>{requestData.secondaryDate ? new Date(requestData.secondaryDate).toLocaleDateString() : "-"}</ValueText>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 1, minWidth: '80px' }}>Issued By:</Typography>
                            <ValueText variant="body1" sx={valueTextStyles}>{user?.username || '—'}</ValueText>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" color="textSecondary" sx={{ ...labelTextStyles, mr: 0, minWidth: '80px' }}>Email:</Typography>
                            <ValueText variant="body1">
                              <a href={`mailto:${user?.email}`} style={{ color: theme.palette.primary.main }}>
                                {user?.email || '—'}
                              </a>
                            </ValueText>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary" sx={labelTextStyles}>Category</Typography>
                          <ValueText variant="body1" sx={valueTextStyles}>
                            {requestData?.categoryName || '—'}
                          </ValueText>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* RIGHT HALF: DESCRIPTION */}
                    <Grid item xs={12} sm={6} pt={0}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary" sx={labelTextStyles}>Description</Typography>
                          <Paper variant="outlined" sx={descriptionPaperStyles(theme)}>
                            <Typography variant="body2" sx={valueTextStyles}>
                              {ingredient?.description || 'No description available'}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary" sx={labelTextStyles}>Additional Info</Typography>
                          <Paper variant="outlined" sx={descriptionPaperStyles(theme)}>
                            <Typography variant="body2" sx={valueTextStyles}>
                              {requestData.additionalInfo && requestData.additionalInfo.trim() !== ""
                                ? requestData.additionalInfo
                                : "No additional information provided."}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="textSecondary" sx={labelTextStyles}>Countries</Typography>
                            {countriesInMyRegions?.length ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {countriesInMyRegions.map((country) => (
                                  <Chip
                                    key={country.id || country.countryCode}
                                    label={`${country.name}`}
                                    size="small"
                                    color="primary"
                                    sx={{ minWidth: 0 }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <ValueText variant="body1" sx={{ ...valueTextStyles, mt: 0.5 }}>
                                No assigned countries
                              </ValueText>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </ContentGridContainer>
      )}

      {/* Status Banner */}
      {isLocked ? (
        <Alert severity="warning" sx={{ mb: 2, mx: 2 }}>
          {lockReason || 'Editing is currently locked.'}
        </Alert>
      ) : isSubmitted ? (
        <Alert severity="success" sx={{ mb: 2, mx: 2 }}>
          You have submitted the product list. R&D members can now start their impact analysis.
          This submission is final and cannot be edited.
        </Alert>
      ) : null}

      {/* Product Assessment Section */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', gap: 1, mt: 0.5 }}>
        <Box sx={{ flex: '1 1 100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <StyledCard sx={{ ...cardTransition(theme), display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', position: 'relative' }}>
            <CardContent sx={{ ...cardContentStyles, py: 0.8, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <SectionTitle variant="h6" sx={sectionTitleStyles(theme)}>
                Product Assessment
              </SectionTitle>

              {/* Bulk Import Section */}
              {isEditMode && !isLocked && (
                <ExcelBulkImport
                  onProductsImported={handleBulkProductsImported}
                  existingProducts={products}
                  availableProducts={availableProducts}
                />
              )}

              {products.length === 0 && isEditMode ? (
                <Paper variant="outlined" sx={{ p: 1, bgcolor: theme.palette.grey[50], flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <InfoIcon sx={{ color: theme.palette.grey[500], mr: 1, fontSize: '1rem' }} />
                    <Typography variant="body2" color="textSecondary" sx={valueTextStyles}>
                      No products added yet. Click Add Product to begin your product list.
                    </Typography>
                  </Box>
                </Paper>
              ) : (
                <ProductTable
                  products={products}
                  onRemove={handleRemoveProduct}
                  onEdit={handleEditProduct}
                  isEditMode={isEditMode && !isLocked}
                />
              )}

              {/* Add Product button */}
              {isEditMode && !isLocked && (
                <Box sx={{
                  position: 'absolute',
                  right: '20px',
                  top: '10px',
                  zIndex: 1,
                }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => setShowAddDialog(true)}
                    sx={addButtonStyles(theme)}
                  >
                    Add Product
                  </Button>
                </Box>
              )}
            </CardContent>
          </StyledCard>
        </Box>
      </Box>

      {/* Submit Buttons */}
      {isEditMode && !isLocked && (
        <SubmitButtonContainer sx={{ pt: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <LoadingButton
              loading={savingDraft}
              variant="outlined"
              size="large"
              onClick={handleSaveDraft}
              disabled={savingDraft || isSubmitted || !hasUnsavedChanges}
              sx={actionButtonStyles(theme)}
              startIcon={<SaveIcon />}
            >
              {isSubmitted ? 'Update Draft' : 'Save as Draft'}
            </LoadingButton>

            <LoadingButton
              loading={submitting && !savingDraft}
              variant="contained"
              size="large"
              onClick={() => setShowSubmitConfirm(true)}
              disabled={savingDraft || products.length === 0 || isLocked}
              sx={actionButtonStyles(theme)}
              startIcon={<SendIcon />}
            >
              Submit to R&D
            </LoadingButton>
          </Box>
        </SubmitButtonContainer>
      )}

      {/* Add Product Dialog */}
      <Dialog open={showAddDialog} onClose={closeAddDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={dialogTitleStyles(theme)}>Add Product</DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={isCustomProduct ? 1 : 0}
            onChange={(_, v) => { setIsCustomProduct(v === 1); setCustomProductErrors({ name: '', cuc: '' }); }}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="Select From List" />
            <Tab label="Add Custom Product" />
          </Tabs>

          {!isCustomProduct ? (
            <Autocomplete
              options={availableProducts.filter(p => !products.some(ep => (ep.productId || ep.id) === p.productId))}
              getOptionLabel={(o) => `${o.productCucNumber} - ${o.productName}`}
              value={selectedProduct}
              onChange={(_, v) => setSelectedProduct(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select From List"
                  fullWidth
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="CUC Number *"
                  size="small"
                  fullWidth
                  value={customProductCUC}
                  onChange={e => setCustomProductCUC(e.target.value)}
                  error={!!customProductErrors.cuc}
                  helperText={customProductErrors.cuc}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Product Name *"
                  size="small"
                  fullWidth
                  value={customProductName}
                  onChange={e => setCustomProductName(e.target.value)}
                  error={!!customProductErrors.name}
                  helperText={customProductErrors.name}
                />
              </Grid>
            </Grid>
          )}

          <TextField
            label="Brand (optional)"
            size="small"
            fullWidth
            value={productBrand}
            onChange={e => setProductBrand(e.target.value)}
            sx={{ mt: 2 }}
          />

        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog} disabled={addingCustomProduct} sx={cancelDialogButtonStyles(theme)}>Cancel</Button>
          <LoadingButton
            loading={addingCustomProduct}
            variant="contained"
            onClick={handleAddProduct}
            disabled={!isCustomProduct && !selectedProduct}
            sx={dialogButtonStyles(theme)}
          >
            Add
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={showEditProductDialog} onClose={handleCancelEditProduct} maxWidth="md" fullWidth>
        <DialogTitle sx={dialogTitleStyles(theme)}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EditIcon sx={{ mr: 1 }} />
            Edit Product
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="CUC Number"
                fullWidth
                size="small"
                value={editingProduct?.productCucNumber || ''}
                disabled
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Product Name"
                fullWidth
                size="small"
                value={editingProduct?.productName || ''}
                disabled
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Brand"
                fullWidth
                size="small"
                value={editProductBrand}
                onChange={(e) => setEditProductBrand(e.target.value)}
                placeholder="Enter brand name (optional)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEditProduct} sx={cancelDialogButtonStyles(theme)}>Cancel</Button>
          <Button onClick={handleSaveEditProduct} variant="contained" color="primary" sx={dialogButtonStyles(theme)}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Submit Confirmation Dialog */}
      <Dialog open={showSubmitConfirm} onClose={() => setShowSubmitConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Submission</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You are about to submit <strong>{products.length}</strong> product(s) to the R&D team.
            They will be notified to start their impact analysis.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This is a single final submission. After confirming, you will not be able to edit or submit this impact assessment again.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitConfirm(false)} sx={cancelDialogButtonStyles(theme)}>Cancel</Button>
          <LoadingButton loading={savingDraft} variant="contained" onClick={handleSubmit} sx={dialogButtonStyles(theme)}>
            Confirm Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

// Mode Switch Component
const ModeSwitch = styled((props) => (
  <MuiSwitch
    focusVisibleClassName=".Mui-focusVisible"
    disableRipple
    {...props}
  />
))(({ theme }) => ({
  width: 34,
  height: 18,
  padding: 0,
  margin: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 1,
    transitionDuration: '250ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main,
        opacity: 0.85,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 16,
    height: 16,
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 10,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 250,
    }),
  },
}));

// Styled components
const StyledCard = styled(Paper)(({ theme }) => ({
  transition: '0.3s',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  height: '100%',
  backgroundColor: '#fcfcfc',
  position: 'relative'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '1.05rem',
  borderBottomWidth: '2px',
  borderBottomColor: theme.palette.grey.light2,
  pb: 0.5,
  mb: 0.75,
}));

const ValueText = styled(Typography)({
  fontSize: '0.9rem',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  whiteSpace: 'normal',
  flex: 1
});

export default CoordinatorResponsePage;
